import './assets/main.css'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import { PiniaColada } from '@pinia/colada'
import * as Sentry from '@sentry/vue'
import App from './App.vue'
import router from './router'
import { registerServiceWorker } from './registerServiceWorker'
import { track, APP_KEY } from '@/lib/usageTracker'
import { getLytAccountingApiBase, getSubscriptionManagerApiBase, getAppApiBase } from './api/baseUrls'

import logoPngUrl from '../designs/Logo.png'

function setFavicon(href: string) {
  try {
    let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    link.type = 'image/png'
    link.href = href
  } catch (e) {
    console.warn('Failed to set favicon to Logo.png', e)
  }
}

if (logoPngUrl) setFavicon(logoPngUrl)

window.lytAccountingApi = getLytAccountingApiBase()
window.subscriptionManagerApi = getSubscriptionManagerApiBase()
window.appApi = getAppApiBase()

const app = createApp(App)

const tracesSampleRateFromEnv = import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE
const tracesSampleRate =
  tracesSampleRateFromEnv !== undefined && tracesSampleRateFromEnv !== ''
    ? Number(tracesSampleRateFromEnv)
    : import.meta.env.PROD
      ? 0.2
      : 1.0

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: Number.isFinite(tracesSampleRate) ? tracesSampleRate : import.meta.env.PROD ? 0.2 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? (import.meta.env.PROD ? 'production' : 'development'),
  })
}

const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)

app.use(pinia)
app.use(PiniaColada)
app.use(router)

router.isReady().then(() => {
  app.mount('#app')
  track('app:Boot', 'view', { appKey: APP_KEY })
  registerServiceWorker()
})

declare global {
  interface Window {
    lytAccountingApi: string
    subscriptionManagerApi: string
    appApi: string
  }
}
